import React, { useState, useEffect } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { loadNoAnswerClients } from '../api';
import NoAnswerClientsList from './NoAnswerClientsList';
import NoAnswerClientsForm from './NoAnswerClientsForm';
import NoAnswerClientsHistory from './NoAnswerClientsHistory';
import NoAnswerClientsQuickActionModal from './NoAnswerClientsQuickActionModal';

const NoAnswerClientsSection = ({ currentUser }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showQuickAction, setShowQuickAction] = useState(false);
  const [quickActionRecord, setQuickActionRecord] = useState(null);
  const { showNotification } = useNotifications();

  // Determinar la URL de la API basada en el entorno
  const API_URL = process.env.NODE_ENV === 'production' 
    ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
    : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

  useEffect(() => {
    loadRecords();
  }, []);

  const loadRecords = async () => {
    try {
      setLoading(true);
      
      // Pasar las células del usuario si requiere filtrado por células
      const userCells = currentUser && currentUser.role !== 'admin' && currentUser.cells ? currentUser.cells : null;
      const userRole = currentUser ? currentUser.role : null;
      const userEmail = currentUser ? currentUser.email : null;
      console.log('🔍 No Answer ClientsSection - Cargando con células del usuario:', userCells);
      console.log('🔍 No Answer ClientsSection - Rol del usuario:', userRole);
      console.log('🔍 No Answer ClientsSection - Email del usuario:', userEmail);
      
      const recordsData = await loadNoAnswerClients(userCells, userRole, userEmail);
      setRecords(recordsData || []);
    } catch (error) {
      console.error('Error loading no answer clients:', error);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    try {
      const url = editingRecord 
        ? `${API_URL}/no-answer-clients/${editingRecord.id}`
        : `${API_URL}/no-answer-clients`;
      
      const method = editingRecord ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          [editingRecord ? 'updated_by' : 'created_by']: currentUser?.firstName && currentUser?.lastName 
            ? `${currentUser.firstName} ${currentUser.lastName}` 
            : currentUser?.email || 'Usuario'
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await loadRecords();
      setShowForm(false);
      setEditingRecord(null);
      showNotification(
        editingRecord 
          ? 'Cliente actualizado exitosamente' 
          : 'Cliente agregado exitosamente', 
        'success'
      );
    } catch (error) {
      console.error('Error saving record:', error);
      showNotification('Error al guardar el cliente', 'error');
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este registro?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/no-answer-clients/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await loadRecords();
      showNotification('Cliente eliminado exitosamente', 'success');
    } catch (error) {
      console.error('Error deleting record:', error);
      showNotification('Error al eliminar el cliente', 'error');
    }
  };

  const handleViewHistory = (record) => {
    setSelectedRecord(record);
    setShowHistory(true);
  };

  const handleQuickAction = (record) => {
    setQuickActionRecord(record);
    setShowQuickAction(true);
  };

  const handleQuickActionSave = async (recordId, actionData) => {
    try {
      // Obtener el registro actual
      const currentRecord = records.find(r => r.id === recordId);
      if (!currentRecord) {
        throw new Error('Registro no encontrado');
      }

      // Actualizar el registro principal si hay cambio de estado
      if (actionData.new_status && actionData.new_status !== actionData.old_status) {
        const updateResponse = await fetch(`${API_URL}/no-answer-clients/${recordId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            nombre_cliente: currentRecord.nombre_cliente,
            motivo: currentRecord.motivo,
            intentos_contacto: currentRecord.intentos_contacto,
            celula: currentRecord.celula,
            campana: currentRecord.campana,
            agente_asignado: currentRecord.agente_asignado,
            detalles_adicionales: currentRecord.detalles_adicionales,
            status: actionData.new_status,
            updated_by: actionData.user_name
          }),
        });

        if (!updateResponse.ok) {
          const errorText = await updateResponse.text();
          throw new Error(`Error ${updateResponse.status}: ${errorText}`);
        }
      }

      // Agregar entrada al historial
      const historyResponse = await fetch(`${API_URL}/no-answer-clients/${recordId}/history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(actionData),
      });

      if (!historyResponse.ok) {
        const errorText = await historyResponse.text();
        throw new Error(`Error en historial ${historyResponse.status}: ${errorText}`);
      }

      // Recargar los datos
      await loadRecords();
      showNotification('Acción registrada exitosamente', 'success');
    } catch (error) {
      console.error('Error saving quick action:', error);
      showNotification(`Error al registrar la acción: ${error.message}`, 'error');
      throw error; // Re-throw para que el modal pueda manejarlo
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              📞 Clientes que no Contestan
            </h2>
            <p className="text-gray-600 mt-1">
              Gestión de clientes que no responden a los intentos de contacto
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors duration-200 flex items-center space-x-2"
          >
            <span>➕</span>
            <span>Agregar Cliente</span>
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="text-yellow-800 text-sm font-medium">Pendientes</div>
            <div className="text-2xl font-bold text-yellow-900">
              {records.filter(r => r.status === 'pendiente').length}
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="text-blue-800 text-sm font-medium">En Proceso</div>
            <div className="text-2xl font-bold text-blue-900">
              {records.filter(r => r.status === 'en_proceso').length}
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="text-green-800 text-sm font-medium">Completados</div>
            <div className="text-2xl font-bold text-green-900">
              {records.filter(r => r.status === 'completado').length}
            </div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <div className="text-red-800 text-sm font-medium">Cancelados</div>
            <div className="text-2xl font-bold text-red-900">
              {records.filter(r => r.status === 'cancelado').length}
            </div>
          </div>
        </div>
      </div>

      {/* Lista de registros */}
      <NoAnswerClientsList
        records={records}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onViewHistory={handleViewHistory}
        onQuickAction={handleQuickAction}
        currentUser={currentUser}
      />

      {/* Formulario */}
      {showForm && (
        <NoAnswerClientsForm
          record={editingRecord}
          onSave={handleSave}
          onClose={() => {
            setShowForm(false);
            setEditingRecord(null);
          }}
          currentUser={currentUser}
        />
      )}

      {/* Historial */}
      {showHistory && selectedRecord && (
        <NoAnswerClientsHistory
          record={selectedRecord}
          onClose={() => {
            setShowHistory(false);
            setSelectedRecord(null);
          }}
        />
      )}

      {/* Modal de Acción Rápida */}
      {showQuickAction && quickActionRecord && (
        <NoAnswerClientsQuickActionModal
          record={quickActionRecord}
          onClose={() => {
            setShowQuickAction(false);
            setQuickActionRecord(null);
          }}
          onSave={handleQuickActionSave}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default NoAnswerClientsSection;
